import React, { useState } from 'react';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import OverviewMetrics from './components/OverviewMetrics';
import StudentCard from './components/StudentCard';
import ProgressAnalytics from './components/ProgressAnalytics';
import CommunicationPanel from './components/CommunicationPanel';
import RecommendationHistory from './components/RecommendationHistory';
import { useAuth } from '../../contexts/AuthContext';

const ParentCounselorDashboard = () => {
  const [selectedStudent, setSelectedStudent] = useState('student-1');
  const [viewMode, setViewMode] = useState('overview'); // overview, progress, communication
  const { userRole } = useAuth();

  const mockStudents = [
    {
      id: 'student-1',
      name: 'Amadou Diallo',
      level: 'Niveau 3 - Explorateur',
      avatar: 'AD',
      currentGrade: 'Terminale S',
      lastActive: '2026-02-11T20:30:00',
      progressPercentage: 65,
      recentActivity: 'Quiz complété: Sciences de l\'Ingénieur'
    },
    {
      id: 'student-2',
      name: 'Fatou Sow',
      level: 'Niveau 2 - Découvreur',
      avatar: 'FS',
      currentGrade: '1ère L',
      lastActive: '2026-02-11T18:15:00',
      progressPercentage: 48,
      recentActivity: 'Nouvelle recommandation: Sciences Humaines'
    },
    {
      id: 'student-3',
      name: 'Moussa Ndiaye',
      level: 'Niveau 4 - Expert',
      avatar: 'MN',
      currentGrade: 'Terminale ES',
      lastActive: '2026-02-11T21:45:00',
      progressPercentage: 82,
      recentActivity: 'Badge débloqué: Économiste en Herbe'
    }
  ];

  const mockOverviewMetrics = [
    {
      icon: 'Users',
      title: 'Étudiants Supervisés',
      value: '3',
      subtitle: 'Actifs ce mois',
      color: 'blue',
      trend: 0
    },
    {
      icon: 'Lightbulb',
      title: 'Recommandations Actives',
      value: '18',
      subtitle: 'Toutes filières',
      color: 'green',
      trend: 12
    },
    {
      icon: 'ClipboardCheck',
      title: 'Quiz Complétés',
      value: '28',
      subtitle: 'Cette semaine',
      color: 'amber',
      trend: 25
    },
    {
      icon: 'TrendingUp',
      title: 'Taux d\'Engagement',
      value: '87%',
      subtitle: 'Moyenne globale',
      color: 'purple',
      trend: 8
    }
  ];

  const currentStudent = mockStudents?.find(s => s?.id === selectedStudent);

  const studentOptions = mockStudents?.map(student => ({
    value: student?.id,
    label: student?.name,
    description: student?.currentGrade
  }));

  const handleGenerateReport = () => {
    console.log('Génération du rapport pour:', currentStudent?.name);
  };

  const handleScheduleSession = () => {
    console.log('Planification d\'une session avec:', currentStudent?.name);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationBar />
      <div className="content-area">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  {userRole === 'parent' ? 'Tableau de Bord Parent' : 'Tableau de Bord Conseiller'}
                </h1>
                <p className="text-sm md:text-base text-gray-600">
                  {userRole === 'parent' ?'Suivez la progression académique et l\'orientation de vos enfants' :'Gérez et suivez vos étudiants avec des outils professionnels'
                  }
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  iconName="Calendar"
                  onClick={handleScheduleSession}
                >
                  Planifier Session
                </Button>
                <Button
                  iconName="FileText"
                  onClick={handleGenerateReport}
                >
                  Générer Rapport
                </Button>
              </div>
            </div>

            {/* View Mode Tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
              <button
                onClick={() => setViewMode('overview')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'overview' ?'bg-primary text-primary-foreground' :'bg-card text-card-foreground hover:bg-muted'
                }`}
              >
                <Icon name="LayoutDashboard" size={16} className="inline mr-2" />
                Vue d'ensemble
              </button>
              <button
                onClick={() => setViewMode('progress')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'progress' ?'bg-primary text-primary-foreground' :'bg-card text-card-foreground hover:bg-muted'
                }`}
              >
                <Icon name="TrendingUp" size={16} className="inline mr-2" />
                Progression Détaillée
              </button>
              <button
                onClick={() => setViewMode('communication')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'communication' ?'bg-primary text-primary-foreground' :'bg-card text-card-foreground hover:bg-muted'
                }`}
              >
                <Icon name="MessageSquare" size={16} className="inline mr-2" />
                Communication
              </button>
            </div>
          </div>

          {/* Overview Metrics */}
          {viewMode === 'overview' && (
            <div className="mb-6">
              <OverviewMetrics metrics={mockOverviewMetrics} />
            </div>
          )}

          {/* Student Selection Section */}
          <div className="mb-6">
            <div className="bg-card rounded-xl p-4 md:p-6 border border-border shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <Icon name="Users" size={24} className="text-primary" />
                <h2 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
                  Sélection de l'Étudiant
                </h2>
              </div>
              <div className="mb-4">
                <Select
                  value={selectedStudent}
                  onChange={setSelectedStudent}
                  options={studentOptions}
                  placeholder="Sélectionner un étudiant"
                  searchable
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {mockStudents?.map((student) => (
                  <StudentCard
                    key={student?.id}
                    student={student}
                    isSelected={student?.id === selectedStudent}
                    onClick={() => setSelectedStudent(student?.id)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Main Content Area - Conditional Rendering */}
          {viewMode === 'overview' && currentStudent && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ProgressAnalytics student={currentStudent} />
              <RecommendationHistory studentId={currentStudent?.id} />
            </div>
          )}

          {viewMode === 'progress' && currentStudent && (
            <div className="space-y-6">
              <ProgressAnalytics student={currentStudent} detailed />
              <RecommendationHistory studentId={currentStudent?.id} />
            </div>
          )}

          {viewMode === 'communication' && currentStudent && (
            <CommunicationPanel student={currentStudent} />
          )}
        </div>
      </div>
    </div>
  );
};

export default ParentCounselorDashboard;